import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  likelyToBeSubmittedShortTermDefault,
  PcrNextPresentationChartEnum,
  upcomingSubmissionBulletsDefault,
} from '../../../models/PcrNextPresentation';
import { getProjectsByCategorization } from '../../../redux/actions/indicatorProjects';
import {
  getFilterBySessionKeyOrGetDefaultSessionObject,
  PCR_NEXT_PRESENTATION,
} from '../../../services/sessionFiltersIndicatorDetailHelper';
import styles from './PcrNextPresentation.module.scss';
import PcrNextPresentationChart from './PcrNextPresentationChart/PcrNextPresentationChart';
import PcrNextPresentationTable from './PcrNextPresentationTable/PcrNextPresentationTable';
import Filter from '../../Portfolio/Filter/Filter';
import FilterIndicators from '../../../models/FilterIndicators';
import { AppMenuTabs } from '../../MenuTabs/userTabsInterface';
import { setIndicatorResponse, setLoading } from '../../../redux/actions/dataTable';
import { translateMultipleText } from '../../../services/stringUtil';
import { getNewFilteredIndicators } from '../../../api/indicatorApi';
import { emptyProjects } from '../../Portfolio/PortfolioUtil';
import { processIndicators } from '../../Portfolio/indicatorHelper';
import { arrayMove } from 'react-sortable-hoc';
import { ProjectProductTypes } from '../../../models/ProjectProductTypes';
import { resetIndicatorDetailFilter } from '../../../redux/actions/indicatorDetailFilters';
import { updateCreatingOrEditing, updateMenuTabsFirstLoad } from '../../../redux/actions/menuTabs';
import { setCurrentIndicator } from '../../../redux/actions/currentIndicator';
import { mockFirstDataGroup, mockSecondDataGroup } from './mockDataNull';

const { container, pcrCharContainer, bodyContainer, tableContainer, extraHeaderTitle, headerTitleStyle } = styles;
const categorizationValues = {
  0: '7',
  1: '8',
};

type Props = {
  headerTitle: any;
};
export const PcrNextPresentation = ({ headerTitle }: Props) => {
  const { upcomingSubmissionTotal, likelyToBeSubmittedShortTermTotal, id, indicatorKey } = useSelector<any, any>(
    state => state.currentIndicator,
  );
  const defaultSessionFilterObject = {
    selectedChart:
      upcomingSubmissionTotal !== 0
        ? PcrNextPresentationChartEnum.UPCOMING_SUBMISSION
        : PcrNextPresentationChartEnum.LIKELY_TO_BE_SUBMITTED_SHORT_TERM,
  };
  // const { firstDataGroup, secondDataGroup } = useSelector<any, any>(state => state?.currentIndicator?.data);
  const { currentIndicator } = useSelector<any, any>(state => state);
  const [chartSelected, setChartSelected] = useState<any>(
    getFilterBySessionKeyOrGetDefaultSessionObject(PCR_NEXT_PRESENTATION, defaultSessionFilterObject).selectedChart,
  );

  const firstDataGroup = currentIndicator?.data?.firstDataGroup ?? mockFirstDataGroup;
  const secondDataGroup = currentIndicator?.data?.secondDataGroup ?? mockSecondDataGroup;

  const { t } = useTranslation();
  const dispatch = useDispatch();

  // filter

  const filterIndicators = useSelector<any, FilterIndicators>(state => state.filterIndicators);
  const stateProjectProductTypes = useSelector<any, FilterIndicators>(
    state => state.indicatorPayload.payload.projectProductTypes,
  );
  const menuTabs = useSelector<any, AppMenuTabs>(state => state.menuTabs);
  const portfolioSelectedTab = menuTabs.portfolioSelectedTab;
  const creatingOrEditing = menuTabs.creatingOrEditing;
  const [isLoading, setIsLoading] = React.useState(false);
  const [totalProjects, setTotalProjects] = React.useState(false);

  const projectTypeFilterChange = {
    ...filterIndicators,
    common: { ...filterIndicators.common, projectTypeFilters: stateProjectProductTypes },
    projectTypeFilters: stateProjectProductTypes,
  };

  const handleCommonFilterChange = async () => {
    dispatch(setLoading(true));
    setIsLoading(true);
    try {
      const indicatorsToProcess = await getNewFilteredIndicators(
        {},
        { projectProductTypes: stateProjectProductTypes },
        {
          ...projectTypeFilterChange.common,
        },
      );
      dispatch(setIndicatorResponse(indicatorsToProcess));
      startProcessIndicators(
        indicatorsToProcess,
        translateMultipleText(` ${' '} ${portfolioSelectedTab.title}`, t),
        filterIndicators.common.projectTypeFilters,
      );
      setTotalProjects(emptyProjects(indicatorsToProcess.totalProjects));
    } catch (err) {
      console.error(err, 'Error fetching data from indicators');
    } finally {
      dispatch(setLoading(false));
      setIsLoading(false);
    }
  };

  const startProcessIndicators = (inds, titleFromTab, projectTypeFilter = [ProjectProductTypes.Loan]) => {
    const result = processIndicators(inds, {
      titleFromTab,
      projectTypeFilter,
    });
    const elementSummary: any = result.find(elem => elem.id === 15);
    const indexSummary: number = result.indexOf(elementSummary);
    const sendEndSummary = arrayMove(result, indexSummary, result.length);

    dispatch(setCurrentIndicator(sendEndSummary?.find(data => data.indicatorKey === 'PCR_NEXT_PRESENTATION')));

    setIsLoading(false);
  };

  // const tabId0OrCreatingEditing = (menuTabs, creatingOrEditing) =>
  //   menuTabs.portfolioSelectedTab.tabId === 0 || creatingOrEditing;

  useEffect(() => {
    if (!sessionStorage.getItem(PCR_NEXT_PRESENTATION)) {
      sessionStorage.setItem(PCR_NEXT_PRESENTATION, JSON.stringify(defaultSessionFilterObject));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getFilteredSimpleIndicators(dispatch, id, categorizationValues[chartSelected], indicatorKey);
    const storage = sessionStorage.getItem(PCR_NEXT_PRESENTATION);
    storage && JSON.parse(storage).selectedChart !== chartSelected && sessionStorage.removeItem('PaginationIndicator');
    storage &&
      sessionStorage.setItem(
        PCR_NEXT_PRESENTATION,
        JSON.stringify({ ...JSON.parse(storage), selectedChart: chartSelected }),
      );
    headerTitle(
      true,
      <p className={headerTitleStyle}>
        {t('upcoming_submission_of_pcr')}
        {chartSelected === PcrNextPresentationChartEnum.LIKELY_TO_BE_SUBMITTED_SHORT_TERM && (
          <p className={extraHeaderTitle}> / {t('likely_to_be_submitted_in_the_short_term')}</p>
        )}
      </p>,
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartSelected, filterIndicators.common]);

  React.useEffect(() => {
    if (
      filterIndicators.common.coReponsibleDivisions.length > 0 ||
      filterIndicators.common.countryId.length > 0 ||
      filterIndicators.common.divisionId.length > 0 ||
      filterIndicators.common.interestIssues.length > 0 ||
      filterIndicators.common.participantDivisions.length > 0 ||
      filterIndicators.common.regionId.length > 0 ||
      filterIndicators.common.sectors.length > 0 ||
      filterIndicators.common.teamLeaders.length > 0 ||
      !menuTabs.portfolioSelectedTab.default
    )
      handleCommonFilterChange();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterIndicators.common, menuTabs.portfolioSelectedTab]);

  React.useEffect(() => {
    dispatch(resetIndicatorDetailFilter());
    if (
      !menuTabs.firstLoad &&
      // tabId0OrCreatingEditing(menuTabs, creatingOrEditing) &&
      filterIndicators.common.coReponsibleDivisions.length === 0 &&
      filterIndicators.common.countryId.length === 0 &&
      filterIndicators.common.divisionId.length === 0 &&
      filterIndicators.common.interestIssues.length === 0 &&
      filterIndicators.common.participantDivisions.length === 0 &&
      filterIndicators.common.regionId.length === 0 &&
      filterIndicators.common.sectors.length === 0 &&
      filterIndicators.common.teamLeaders.length === 0 &&
      menuTabs.portfolioSelectedTab.default
    ) {
      handleCommonFilterChange();
      if (creatingOrEditing) {
        dispatch(updateCreatingOrEditing(false));
      }
    }
    if (menuTabs.firstLoad) {
      dispatch(updateMenuTabsFirstLoad(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuTabs, filterIndicators.common]);

  const handleClick = clickedValue => {
    if (clickedValue === PcrNextPresentationChartEnum.UPCOMING_SUBMISSION && upcomingSubmissionTotal > 0) {
      setChartSelected(clickedValue);
    }
    if (
      clickedValue === PcrNextPresentationChartEnum.LIKELY_TO_BE_SUBMITTED_SHORT_TERM &&
      likelyToBeSubmittedShortTermTotal > 0
    ) {
      setChartSelected(clickedValue);
    }
  };
  return (
    <div className={container}>
      <div className={pcrCharContainer}>
        <PcrNextPresentationChart
          title={t('new_PCR_in_progress')}
          data={firstDataGroup}
          selected={chartSelected === PcrNextPresentationChartEnum.UPCOMING_SUBMISSION}
          handleClick={() => handleClick(PcrNextPresentationChartEnum.UPCOMING_SUBMISSION)}
          bulletsData={upcomingSubmissionBulletsDefault}
          unselectable={upcomingSubmissionTotal === 0}
        />
        <PcrNextPresentationChart
          title={t('new_PCR_close_to_starting_in_the_short_term')}
          data={secondDataGroup}
          selected={chartSelected === PcrNextPresentationChartEnum.LIKELY_TO_BE_SUBMITTED_SHORT_TERM}
          handleClick={() => handleClick(PcrNextPresentationChartEnum.LIKELY_TO_BE_SUBMITTED_SHORT_TERM)}
          bulletsData={likelyToBeSubmittedShortTermDefault}
          unselectable={likelyToBeSubmittedShortTermTotal === 0}
        />
      </div>
      <div className={bodyContainer}>
        {/* <TableHeader
          titleKey={
            chartSelected === PcrNextPresentationChartEnum.UPCOMING_SUBMISSION
              ? 'upcoming_submission'
              : 'likely_to_be_submitted_in_the_short_term'
          }
        /> */}
        <div className={tableContainer}>
          <PcrNextPresentationTable
            isUpcommingPresentationTable={chartSelected === PcrNextPresentationChartEnum.UPCOMING_SUBMISSION}
          />
        </div>
      </div>
    </div>
  );
};

const getFilteredSimpleIndicators = (d, currentIndicatorId, categorization, indicatorKey) => {
  return d(getProjectsByCategorization(currentIndicatorId, categorization, { indicatorKey }));
};
